import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  MONOMER_ANALOGUE_COMPATIBILITY,
  MONOMER_ANALOGUE_DESCRIPTOR,
  MONOMER_ANALOGUE_DISPLAY_NAME,
  ORDERED_ANALOGUES,
  ORDERED_DEGENERATE_ANALOGUES,
} from 'molbio/monomers/MonomerConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

function getDisplayName(selected: Array<MonomerNaturalAnalogue>): string {
  if (selected.length === 0) {
    return 'Filter by natural analog';
  }
  if (selected.length === 2) {
    return 'Filtering by: Any sugar';
  }
  if (selected.length === 5) {
    return 'Filtering by: Any base';
  }
  return `Filtering by: ${MONOMER_ANALOGUE_DISPLAY_NAME[selected[0]]}`;
}

export const NATURAL_ANALOGUE_SEARCH_OPTIONS = [
  {
    name: 'Any natural analog',
    value: [],
  },
  {
    name: 'Any sugar',
    value: [MonomerNaturalAnalogue.D_LOWER, MonomerNaturalAnalogue.R_LOWER],
  },
  {
    name: 'Any base',
    value: [
      MonomerNaturalAnalogue.A,
      MonomerNaturalAnalogue.C,
      MonomerNaturalAnalogue.G,
      MonomerNaturalAnalogue.T,
      MonomerNaturalAnalogue.U,
    ],
  },
  ...ORDERED_ANALOGUES.map((na) => ({
    name: MONOMER_ANALOGUE_DISPLAY_NAME[na],
    value: [na],
    metadata: MONOMER_ANALOGUE_DESCRIPTOR[na],
  })),
  {name: 'Degenerate', isHeader: true},
  ...ORDERED_DEGENERATE_ANALOGUES.map((na) => ({
    name: MONOMER_ANALOGUE_DISPLAY_NAME[na],
    value: [na],
    metadata: `${MONOMER_ANALOGUE_DESCRIPTOR[na]} ${MONOMER_ANALOGUE_COMPATIBILITY[na]}`,
  })),
];

export interface MonomerListingFiltersProps {
  archivePurposes: Array<string>;
  handleArchivePurposeFilter: (purposes: Array<string>) => void;
  handleNaturalAnalogueFilter: (nas: Array<MonomerNaturalAnalogue>) => void;
  handleQueryFilter: (query: string) => void;
  monomerIds: Array<string>;
  naturalAnalogues: Array<MonomerNaturalAnalogue>;
  query: string;
}

export default function MonomerListingFilters({
  archivePurposes,
  handleNaturalAnalogueFilter,
  handleArchivePurposeFilter,
  handleQueryFilter,
  monomerIds,
  naturalAnalogues,
  query,
}: MonomerListingFiltersProps): JSX.Element {
  return (
    <div className={css(styles.filters)}>
      <PlainSearchInput
        className={css(styles.search)}
        clearType={ClearType.X}
        onChange={(e: InputChangeEvent) => {
          handleQueryFilter(e.target.value);
        }}
        onEscape={() => {
          handleQueryFilter('');
        }}
        placeholder="Monomer name or symbol"
        value={query}
      />
      <SelectDropdown
        className={css(styles.dropdown)}
        menu={
          <SearchableMenu
            width={MenuWidth.SMALL}
            selectedValue={naturalAnalogues}
            optionDatas={NATURAL_ANALOGUE_SEARCH_OPTIONS}
            onSelect={handleNaturalAnalogueFilter}
          />
        }
        displayName={getDisplayName(naturalAnalogues)}
        dataTestAttrs={{component: 'MonomerListingFilters', element: 'naturalAnalogueDropdown'}}
      />
      <ArchivePurposeDropdown
        selectedPurposes={archivePurposes}
        onChangePurposes={handleArchivePurposeFilter}
        itemIds={monomerIds}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  filters: {
    marginTop: '20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    marginRight: '10px',
    marginTop: '6px',
    marginBottom: '6px',
    width: 267,
    verticalAlign: 'middle',
  },
  dropdown: {
    display: 'flex',
    verticalAlign: 'middle',
    marginRight: '10px',
  },
});
